import type { ContentRegistry } from '../content/registry';
import type { InputFrame } from '../core/input';
import { restoreConfig, storeConfig } from '../meta/runPersistence';
import { INTRO_SECONDS, Sim, type SimCommand } from '../sim/sim';
import type { NetChannel } from './channel';
import { buildCoopRunConfig, MAX_COOP_PLAYERS, sanitizeLoadout } from './coopConfig';
import { LockstepGuest, LockstepHost, INPUT_DELAY, MAX_DELAY } from './lockstep';
import { NET_PROTOCOL_VERSION, type CoopLoadout, type LobbySettings, type RosterEntry } from './protocol';
import type { LobbyTransport } from './webrtc';

/**
 * Online co-op session: the lobby (roster, loadouts, ready-up, host settings)
 * and, once started, the lockstep run. The Game owns one session at a time and
 * talks to it only through this API plus the callbacks below; everything
 * network-shaped stays in src/net.
 */

export interface CoopCallbacks {
  /** Roster or settings changed (lobby screen re-renders). */
  onLobby(): void;
  /** The run began: `sim` is ready, `index` is the local player. */
  onStart(sim: Sim, index: number, names: string[]): void;
  /** A player left mid-run (they idle from now on). */
  onPlayerLeft(index: number, name: string): void;
  /** The session is over (host left, kicked, connection lost). */
  onEnded(reason: string): void;
  /** A dropped player reconnected mid-run and took their character back. */
  onPlayerRejoined?(index: number, name: string): void;
}

/** Stable per-tab key so a player who reloads can reclaim their slot mid-run. */
export function rejoinKey(code: string): string {
  const storageKey = `chronofall.coopKey.${code}`;
  try {
    const existing = sessionStorage.getItem(storageKey);
    if (existing) return existing;
    const fresh = Array.from(crypto.getRandomValues(new Uint8Array(12)), (b) => b.toString(16).padStart(2, '0')).join('');
    sessionStorage.setItem(storageKey, fresh);
    return fresh;
  } catch {
    return Array.from(crypto.getRandomValues(new Uint8Array(12)), (b) => b.toString(16).padStart(2, '0')).join('');
  }
}

const PING_EVERY_MS = 1000;
const DELAY_CHECK_MS = 2000;
const now = () => performance.now();

interface PeerLink {
  id: string;
  channel: NetChannel;
  loadout: CoopLoadout | null;
  ready: boolean;
  rtt: number | null;
  pingN: number;
  pingSentAt: number;
  index: number;
  /** Rejoin key the guest sent at hello. */
  key: string;
}

abstract class CoopSessionBase {
  abstract readonly role: 'host' | 'guest';
  sim: Sim | null = null;
  localIndex = 0;
  names: string[] = [];
  ended = false;
  protected lastPingAt = 0;

  constructor(protected readonly reg: ContentRegistry, protected readonly gameVersion: string, protected readonly cb: CoopCallbacks) {}

  abstract get lockstep(): LockstepHost | LockstepGuest | null;
  abstract step(local: InputFrame): number;
  abstract leave(): void;
  /** One-line connection summary for the HUD, and whether to show it as a warning. */
  abstract netStatus(): { text: string; warn: boolean };
  /** Multi-line detail for the debug overlay's net panel. */
  abstract netDebug(): string[];

  issue(cmd: SimCommand): void {
    this.lockstep?.issue(cmd);
  }

  protected end(reason: string): void {
    if (this.ended) return;
    this.ended = true;
    this.cb.onEnded(reason);
  }
}

/* -------------------------------------------------------------------------- */

export class CoopHostSession extends CoopSessionBase {
  readonly role = 'host' as const;
  code: string | null = null;
  settings: LobbySettings;
  private peers: PeerLink[] = [];
  private host: LockstepHost | null = null;
  private nextPeerId = 1;
  private started = false;
  /** The run's config as sent to guests (re-sent to a player who rejoins). */
  private storedConfig: ReturnType<typeof storeConfig> | null = null;
  /** Rejoin key of each player slot at start. */
  private slotKeys: string[] = [];

  constructor(reg: ContentRegistry, gameVersion: string, cb: CoopCallbacks, private transport: LobbyTransport, public loadout: CoopLoadout, settings: LobbySettings) {
    super(reg, gameVersion, cb);
    this.settings = settings;
  }

  get lockstep(): LockstepHost | null {
    return this.host;
  }

  async open(): Promise<string> {
    this.code = await this.transport.host(
      (channel) => this.admit(channel),
      (message) => this.end(`The lobby service stopped responding: ${message}`),
    );
    return this.code;
  }

  roster(): RosterEntry[] {
    const entry = (id: string, l: CoopLoadout, ready: boolean, host: boolean, ping: number | null): RosterEntry => ({
      id, name: l.name, character: l.character, weapon: l.equipment.find((e) => this.reg.items.get(e)?.slot === 'weapon') ?? 'w_spore_launcher', pet: l.pet, ready, host, ping,
    });
    return [entry('host', this.loadout, true, true, null), ...this.peers.filter((p) => p.loadout).map((p) => entry(p.id, p.loadout!, p.ready, false, p.rtt))];
  }

  /** Everyone connected has sent a loadout and pressed Ready, and there is someone to play with. */
  canStart(): boolean {
    const joined = this.peers.filter((p) => p.loadout);
    return !this.started && joined.length >= 1 && joined.every((p) => p.ready);
  }

  setSettings(settings: LobbySettings): void {
    this.settings = settings;
    this.broadcastLobby();
  }

  setLoadout(loadout: CoopLoadout): void {
    this.loadout = loadout;
    this.broadcastLobby();
  }

  private admit(channel: NetChannel): void {
    // Mid-run, a connection is only useful as a rejoin; that's decided at hello.
    if (this.ended || (!this.started && this.peers.length >= MAX_COOP_PLAYERS - 1)) {
      channel.send({ t: 'reject', reason: 'That lobby is full.' });
      setTimeout(() => channel.close('rejected'), 500);
      return;
    }
    const peer: PeerLink = { id: `g${this.nextPeerId++}`, channel, loadout: null, ready: false, rtt: null, pingN: 0, pingSentAt: 0, index: -1, key: '' };
    this.peers.push(peer);
    channel.onMessage = (m) => this.onMessage(peer, m);
    channel.onClose = () => this.onPeerClosed(peer);
  }

  private onPeerClosed(peer: PeerLink): void {
    if (this.started) {
      if (peer.index > 0) this.host?.peerClosed(peer.index, peer.channel);
      else this.peers = this.peers.filter((p) => p !== peer); // a failed rejoin attempt
      return;
    }
    this.peers = this.peers.filter((p) => p !== peer);
    this.broadcastLobby();
  }

  private onMessage(peer: PeerLink, msg: unknown): void {
    if (!msg || typeof msg !== 'object') return;
    const m = msg as Record<string, unknown>;
    switch (m.t) {
      case 'ping':
        peer.channel.send({ t: 'pong', n: m.n });
        return;
      case 'pong':
        if (m.n === peer.pingN) peer.rtt = Math.round(now() - peer.pingSentAt);
        return;
      case 'hello': {
        if (m.protocol !== NET_PROTOCOL_VERSION || m.gameVersion !== this.gameVersion) {
          peer.channel.send({ t: 'reject', reason: `Version mismatch — the host is on v${this.gameVersion}. Refresh the page to update.` });
          setTimeout(() => peer.channel.close('version'), 500);
          return;
        }
        const loadout = sanitizeLoadout(m.loadout, this.reg);
        if (!loadout) return;
        peer.key = typeof m.key === 'string' ? m.key.slice(0, 64) : '';
        if (this.started) {
          this.rejoin(peer, loadout);
          return;
        }
        peer.loadout = loadout;
        peer.channel.send({ t: 'welcome' });
        this.broadcastLobby();
        return;
      }
      case 'loadout': {
        const loadout = sanitizeLoadout(m.loadout, this.reg);
        if (loadout && !this.started) {
          peer.loadout = loadout;
          peer.ready = false;
          this.broadcastLobby();
        }
        return;
      }
      case 'ready':
        if (!this.started) {
          peer.ready = m.ready === true;
          this.broadcastLobby();
        }
        return;
      case 'leave':
        peer.channel.close('left');
        return;
      default:
        if (this.started && peer.index > 0) this.host?.receive(peer.index, m);
    }
  }

  /**
   * Mid-run hello: hand a dropped slot back to whoever held it (matched by the
   * per-tab rejoin key, then by name, then the only open slot), send them the
   * run config and a full snapshot, and resume their input from the next frame.
   */
  private rejoin(peer: PeerLink, loadout: CoopLoadout): void {
    const name = loadout.name;
    const host = this.host;
    const open = host ? host.dropped.map((d, i) => (d && i > 0 ? i : -1)).filter((i) => i > 0) : [];
    let slot = open.find((i) => peer.key && this.slotKeys[i] === peer.key) ?? open.find((i) => this.names[i] === name) ?? (open.length === 1 ? open[0] : undefined);
    if (!host || slot === undefined || !this.storedConfig) {
      peer.channel.send({ t: 'reject', reason: open.length ? 'That run has already started.' : 'That run has already started and every player is still connected.' });
      setTimeout(() => peer.channel.close('rejected'), 500);
      return;
    }
    // Retire any stale link that held this slot.
    this.peers = this.peers.filter((p) => p === peer || p.index !== slot);
    peer.index = slot;
    peer.loadout = loadout;
    this.slotKeys[slot] = peer.key;
    peer.channel.send({ t: 'welcome' });
    peer.channel.send({ t: 'start', config: this.storedConfig, index: slot, names: this.names, delay: host.delay });
    host.readmit(slot, peer.channel); // sends the resync snapshot
    const who = this.names[slot] ?? `Player ${slot + 1}`;
    host.broadcast({ t: 'rejoined', player: slot, name: who });
    this.cb.onPlayerRejoined?.(slot, who);
  }

  private broadcastLobby(): void {
    if (this.started) return;
    const roster = this.roster();
    for (const p of this.peers) if (p.loadout) p.channel.send({ t: 'lobby', roster, settings: this.settings, you: p.id });
    this.cb.onLobby();
  }

  /** Build the shared config, tell every guest their player index, and start the lockstep. */
  start(): void {
    if (!this.canStart()) return;
    this.started = true;
    // The code stays open during the run so a dropped player can rejoin; poll gently.
    this.transport.setPollInterval?.(4000);
    const joined = this.peers.filter((p) => p.loadout);
    // Anyone who connected but never said hello is dropped now.
    for (const p of this.peers) if (!p.loadout) p.channel.close('run started');
    this.peers = joined;
    const loadouts = [this.loadout, ...joined.map((p) => p.loadout!)];
    const seed = crypto.getRandomValues(new Uint32Array(1))[0];
    const stored = storeConfig({ ...buildCoopRunConfig(this.reg, loadouts, this.settings, seed), introSeconds: INTRO_SECONDS });
    this.storedConfig = stored;
    this.names = loadouts.map((l) => l.name);
    this.slotKeys = ['host', ...joined.map((p) => p.key)];
    joined.forEach((p, i) => {
      p.index = i + 1;
      p.channel.send({ t: 'start', config: stored, index: p.index, names: this.names, delay: INPUT_DELAY });
    });
    // The host simulates the round-tripped config too, so every peer starts from identical bytes.
    const sim = new Sim(this.reg, restoreConfig(stored, this.reg));
    const channels: (NetChannel | null)[] = [null, ...joined.map((p) => p.channel)];
    this.host = new LockstepHost(sim, channels, now);
    this.host.onPlayerLeft = (index) => {
      const name = this.names[index] ?? `Player ${index + 1}`;
      this.host?.broadcast({ t: 'left', player: index, name });
      this.cb.onPlayerLeft(index, name);
    };
    this.sim = sim;
    this.localIndex = 0;
    this.cb.onStart(sim, 0, this.names);
  }

  step(local: InputFrame): number {
    this.ping();
    this.adaptDelay();
    return this.host ? this.host.step(local) : 0;
  }

  private lastDelayCheck = 0;
  private lowerVotes = 0;

  /**
   * Adaptive input delay: size the whole party's delay to the slowest active
   * link (≈ its round trip in frames + 1, within MIN/MAX_DELAY). Rises at once;
   * falls only after three consecutive checks agree, to avoid flapping.
   */
  private adaptDelay(): void {
    const host = this.host;
    const t = now();
    if (!host || t - this.lastDelayCheck < DELAY_CHECK_MS) return;
    this.lastDelayCheck = t;
    const rtts = this.peers.filter((p) => p.index > 0 && !host.dropped[p.index] && p.rtt !== null).map((p) => p.rtt!);
    if (!rtts.length) return;
    const wanted = Math.max(INPUT_DELAY, Math.min(MAX_DELAY, Math.ceil(Math.max(...rtts) / (1000 / 60)) + 1));
    if (wanted > host.delay) this.lowerVotes = 0;
    else if (wanted <= host.delay - 2) {
      if (++this.lowerVotes < 3) return;
      this.lowerVotes = 0;
    } else {
      this.lowerVotes = 0;
      return;
    }
    host.setDelay(wanted);
    host.broadcast({ t: 'delay', d: host.delay });
  }

  /**
   * After a run: keep the party (and the same join code) together, clear ready
   * flags, and go back to admitting new players until the next start.
   */
  async resetForLobby(): Promise<void> {
    if (!this.started || this.ended) return;
    this.started = false;
    this.host = null;
    this.sim = null;
    this.storedConfig = null;
    this.transport.setPollInterval?.(1500);
    // Only guests still connected to a player slot stay in the party.
    this.peers = this.peers.filter((p) => p.channel.isOpen && p.index > 0 && p.loadout !== null);
    for (const p of this.peers) {
      p.ready = false;
      p.index = -1;
    }
    this.broadcastLobby();
  }

  private ping(): void {
    const t = now();
    if (t - this.lastPingAt < PING_EVERY_MS) return;
    this.lastPingAt = t;
    for (const p of this.peers) {
      p.pingN++;
      p.pingSentAt = t;
      p.channel.send({ t: 'ping', n: p.pingN });
    }
  }

  /** Lobby keepalive (the lobby screen calls this on a timer so pings show before the run). */
  tickLobby(): void {
    this.ping();
    if (!this.started && this.peers.some((p) => p.loadout)) this.broadcastLobby();
  }

  netStatus(): { text: string; warn: boolean } {
    const rtts = this.peers.filter((p) => p.index > 0 && !this.host?.dropped[p.index]).map((p) => p.rtt ?? 0);
    const waiting = this.host?.waitingOn ?? [];
    if (waiting.length) return { text: `Waiting for ${waiting.map((i) => this.names[i]).join(', ')}…`, warn: true };
    return { text: `Host · ${rtts.length ? `${Math.max(...rtts)} ms` : 'solo'}`, warn: rtts.some((r) => r > 180) };
  }

  netDebug(): string[] {
    const host = this.host;
    if (!host) return [];
    const s = host.stats;
    const lines = [`net HOST · code ${this.code ?? '—'} · delay ${host.delay}f · frame ${host.nextExec} (lead ${host.localFrame - host.nextExec})`];
    for (let i = 1; i < this.names.length; i++) {
      const peer = this.peers.find((p) => p.index === i);
      const state = host.dropped[i] ? 'DROPPED' : host.isLagging(i) ? 'lagging (stood in)' : 'ok';
      lines.push(`  P${i + 1} ${this.names[i]} · ${peer?.rtt ?? '—'} ms · ${state}`);
    }
    lines.push(`  stand-ins ${s.substitutions} · desyncs ${s.desyncs} · resyncs sent ${s.resyncs}${host.waitingOn.length ? ` · waiting on ${host.waitingOn.map((i) => this.names[i]).join(', ')}` : ''}`);
    return lines;
  }

  leave(): void {
    if (this.ended) return;
    this.ended = true;
    this.transport.stopHosting();
    for (const p of this.peers) {
      p.channel.send({ t: 'end', reason: 'The host ended the session.' });
      p.channel.close('host left');
    }
  }
}

/* -------------------------------------------------------------------------- */

export class CoopGuestSession extends CoopSessionBase {
  readonly role = 'guest' as const;
  rosterEntries: RosterEntry[] = [];
  /** The host went back to the lobby while this guest still holds a finished (or finishing) run. */
  hostInLobby = true;
  settings: LobbySettings = { startEra: 0, paradox: 0 };
  myId = '';
  ready = false;
  private channel: NetChannel | null = null;
  private guest: LockstepGuest | null = null;
  private rtt: number | null = null;
  private pingN = 0;
  private pingSentAt = 0;

  constructor(reg: ContentRegistry, gameVersion: string, cb: CoopCallbacks, private transport: LobbyTransport, public loadout: CoopLoadout) {
    super(reg, gameVersion, cb);
  }

  get lockstep(): LockstepGuest | null {
    return this.guest;
  }

  /** Connect, say hello, and resolve once the host has welcomed us. */
  async join(code: string, onStatus: (text: string) => void): Promise<void> {
    const channel = await this.transport.join(code, this.loadout.name, onStatus);
    this.channel = channel;
    onStatus('Joining the lobby…');
    await new Promise<void>((resolve, reject) => {
      let settled = false;
      const settle = (error?: Error) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (error) reject(error);
        else resolve();
      };
      const timer = setTimeout(() => settle(new Error('The host did not respond.')), 10000);
      channel.onMessage = (msg) => {
        const m = msg as Record<string, unknown>;
        if (m?.t === 'welcome') {
          channel.onMessage = (x) => this.onMessage(x);
          settle();
        } else if (m?.t === 'reject') {
          settle(new Error(String(m.reason ?? 'The host declined.')));
          channel.close('rejected');
        }
      };
      channel.onClose = () => settle(new Error('The connection closed before the host answered.'));
      channel.send({ t: 'hello', protocol: NET_PROTOCOL_VERSION, gameVersion: this.gameVersion, loadout: this.loadout, key: rejoinKey(code) });
    });
    channel.onClose = () => this.end(this.sim ? 'The host disconnected — the run is over.' : 'The host closed the lobby.');
  }

  setReady(ready: boolean): void {
    this.ready = ready;
    this.channel?.send({ t: 'ready', ready });
  }

  setLoadout(loadout: CoopLoadout): void {
    this.loadout = loadout;
    this.ready = false;
    this.channel?.send({ t: 'loadout', loadout });
  }

  private onMessage(msg: unknown): void {
    if (!msg || typeof msg !== 'object') return;
    const m = msg as Record<string, unknown>;
    switch (m.t) {
      case 'ping':
        this.channel?.send({ t: 'pong', n: m.n });
        return;
      case 'pong':
        if (m.n === this.pingN) this.rtt = Math.round(now() - this.pingSentAt);
        return;
      case 'lobby':
        this.hostInLobby = true;
        if (Array.isArray(m.roster)) this.rosterEntries = m.roster as RosterEntry[];
        if (m.settings && typeof m.settings === 'object') this.settings = m.settings as LobbySettings;
        if (typeof m.you === 'string') this.myId = m.you;
        this.ready = this.rosterEntries.find((r) => r.id === this.myId)?.ready ?? this.ready;
        this.cb.onLobby();
        return;
      case 'start':
        this.begin(m);
        return;
      case 'left':
        this.cb.onPlayerLeft(Number(m.player), String(m.name ?? 'A player'));
        return;
      case 'rejoined':
        this.cb.onPlayerRejoined?.(Number(m.player), String(m.name ?? 'A player'));
        return;
      case 'end':
        this.end(String(m.reason ?? 'The host ended the session.'));
        this.channel?.close('ended');
        return;
      case 'reject':
        this.end(String(m.reason ?? 'The host removed you from the lobby.'));
        return;
      default:
        this.guest?.receive(m);
    }
  }

  /** Drop the finished run's state so the next `start` can begin a new one. */
  resetForLobby(): void {
    this.sim = null;
    this.guest = null;
    this.ready = false;
  }

  /** Lobby keepalive + ping (the lobby screen calls this on a timer). */
  tickLobby(): void {
    const t = now();
    if (t - this.lastPingAt < PING_EVERY_MS || !this.channel) return;
    this.lastPingAt = t;
    this.pingN++;
    this.pingSentAt = t;
    this.channel.send({ t: 'ping', n: this.pingN });
  }

  private begin(m: Record<string, unknown>): void {
    if (!this.channel) return;
    this.hostInLobby = false;
    const index = Number(m.index);
    const config = restoreConfig(m.config as never, this.reg);
    if (!Number.isInteger(index) || index < 1 || index >= config.playerCount) return;
    const sim = new Sim(this.reg, config);
    this.guest = new LockstepGuest(sim, this.channel, index, now, Number(m.delay) || INPUT_DELAY);
    this.sim = sim;
    this.localIndex = index;
    this.names = Array.isArray(m.names) ? (m.names as unknown[]).map(String) : [];
    this.cb.onStart(sim, index, this.names);
  }

  step(local: InputFrame): number {
    const t = now();
    if (t - this.lastPingAt >= PING_EVERY_MS && this.channel) {
      this.lastPingAt = t;
      this.pingN++;
      this.pingSentAt = t;
      this.channel.send({ t: 'ping', n: this.pingN });
    }
    return this.guest ? this.guest.step(local) : 0;
  }

  /** ms the guest has been starved of host frames (0 when flowing). */
  stalledFor(): number {
    return this.guest?.stalledFor() ?? 0;
  }

  netDebug(): string[] {
    const g = this.guest;
    if (!g) return [];
    const s = g.stats;
    return [
      `net GUEST P${this.localIndex + 1} · ${this.rtt ?? '—'} ms · delay ${g.delay}f · frame ${g.nextExec} (lead ${g.localFrame - g.nextExec})`,
      `  buffered ${g.bufferDepth} · rebuffers ${s.rebuffers} · resyncs ${s.resyncs} · stalled ${Math.round(this.stalledFor())} ms`,
    ];
  }

  netStatus(): { text: string; warn: boolean } {
    const stalled = this.stalledFor();
    if (stalled > 400) return { text: 'Waiting for host…', warn: true };
    return { text: `Online · ${this.rtt ?? '—'} ms`, warn: (this.rtt ?? 0) > 180 };
  }

  leave(): void {
    if (this.ended) return;
    this.ended = true;
    this.channel?.send({ t: 'leave' });
    const ch = this.channel;
    setTimeout(() => ch?.close('left'), 200);
  }
}

export type CoopSession = CoopHostSession | CoopGuestSession;
